import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface Question {
  id: number;
  subject: string;
  topic: string;
  difficulty: string;
  questionText: string;
  options: string[];
  marks: number;
}

interface SessionState {
  sessionId: number;
  questions: Question[];
  mode: string;
}

const PracticeSessionPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionData = location.state as SessionState;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showHint, setShowHint] = useState(false);
  const [hint, setHint] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanation, setExplanation] = useState('');
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [xpEarned, setXpEarned] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());

  useEffect(() => {
    if (!sessionData) {
      navigate('/practice');
    }
  }, [sessionData, navigate]);

  if (!sessionData) {
    return null;
  }

  const currentQuestion = sessionData.questions[currentIndex];
  const progress = ((currentIndex + 1) / sessionData.questions.length) * 100;

  const handleAnswer = async (answer: string) => {
    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);

    try {
      const response = await api.post('/practice/answer', {
        sessionId: sessionData.sessionId,
        questionId: currentQuestion.id,
        answer,
        timeTaken,
        hintUsed: showHint,
      });

      setAnswers({ ...answers, [currentQuestion.id]: answer });
      setIsCorrect(response.data.data.isCorrect);
      setExplanation(response.data.data.explanation);
      setXpEarned(xpEarned + response.data.data.xpEarned);
      setShowExplanation(true);
    } catch (error) {
      console.error('Failed to submit answer:', error);
      alert('Failed to submit answer. Please try again.');
    }
  };

  const handleNext = () => {
    if (currentIndex < sessionData.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowHint(false);
      setShowExplanation(false);
      setIsCorrect(null);
      setQuestionStartTime(Date.now());
    } else {
      handleComplete();
    }
  };

  const handleSkip = async () => {
    const timeTaken = Math.floor((Date.now() - questionStartTime) / 1000);

    try {
      await api.post('/practice/answer', {
        sessionId: sessionData.sessionId,
        questionId: currentQuestion.id,
        answer: null,
        timeTaken,
        hintUsed: false,
      });

      handleNext();
    } catch (error) {
      console.error('Failed to skip question:', error);
    }
  };

  const handleHint = async () => {
    if (showHint) {
      setShowHint(false);
      return;
    }

    try {
      const response = await api.post('/practice/hint', {
        questionId: currentQuestion.id,
      });
      setHint(response.data.data.hint);
      setShowHint(true);
    } catch (error) {
      console.error('Failed to get hint:', error);
    }
  };

  const handleComplete = async () => {
    try {
      const response = await api.post('/practice/complete', {
        sessionId: sessionData.sessionId,
      });

      navigate('/practice/summary', {
        state: response.data.data,
      });
    } catch (error) {
      console.error('Failed to complete session:', error);
      alert('Failed to complete session. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Question {currentIndex + 1} of {sessionData.questions.length}
              </h1>
              <p className="text-sm text-gray-600 capitalize">
                {currentQuestion.subject} • {currentQuestion.topic} • {currentQuestion.difficulty}
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{xpEarned} XP</p>
              <p className="text-sm text-gray-600">Earned</p>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1">
              <div className="text-lg text-gray-900 leading-relaxed whitespace-pre-wrap">
                {currentQuestion.questionText}
              </div>
            </div>
            <div className="ml-4 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {currentQuestion.marks} marks
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              const optionLetter = String.fromCharCode(65 + index);
              const isSelected = answers[currentQuestion.id] === optionLetter;
              const isDisabled = showExplanation;

              return (
                <button
                  key={index}
                  onClick={() => !isDisabled && handleAnswer(optionLetter)}
                  disabled={isDisabled}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? isCorrect === true
                        ? 'border-green-500 bg-green-50'
                        : isCorrect === false
                        ? 'border-red-500 bg-red-50'
                        : 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  } ${isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start">
                    <span className="font-semibold text-gray-700 mr-3">{optionLetter}.</span>
                    <span className="text-gray-900">{option}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Hint */}
          {showHint && (
            <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <div className="flex items-start">
                <span className="text-2xl mr-3">💡</span>
                <div>
                  <h3 className="font-semibold text-yellow-900 mb-1">Hint</h3>
                  <p className="text-yellow-800">{hint}</p>
                </div>
              </div>
            </div>
          )}

          {/* Explanation */}
          {showExplanation && (
            <div className={`mt-6 p-4 border-l-4 rounded ${
              isCorrect
                ? 'bg-green-50 border-green-400'
                : 'bg-red-50 border-red-400'
            }`}>
              <div className="flex items-start">
                <span className="text-2xl mr-3">{isCorrect ? '✅' : '❌'}</span>
                <div className="flex-1">
                  <h3 className={`font-semibold mb-2 ${
                    isCorrect ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {isCorrect ? 'Correct!' : 'Incorrect'}
                  </h3>
                  <div className={`whitespace-pre-wrap ${
                    isCorrect ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {explanation}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            {!showExplanation && (
              <>
                <button
                  onClick={handleHint}
                  className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium"
                >
                  {showHint ? 'Hide Hint' : '💡 Get Hint'}
                </button>
                <button
                  onClick={handleSkip}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Skip →
                </button>
              </>
            )}
          </div>
          {showExplanation && (
            <button
              onClick={handleNext}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {currentIndex < sessionData.questions.length - 1 ? 'Next Question →' : 'Complete Practice'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PracticeSessionPage;
